import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { cardAttributes, navLabels } from '@sorare/core/src/lib/glossary';
import {
  PlayerSearchViewAttributes,
  StatsOnboarding,
} from '@sorare/core/src/lib/playerSearchView';
import {
  averageScoreSorts,
  breakdownScoreSorts,
  projectionSorts,
} from '@sorare/core/src/lib/scouting-center';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { AveragePlayerScoreLabel } from '../AveragePlayerScoreLabel';
import { BenchObjectTableHeader } from '../BenchObjectTableHeader';
import {
  MatchupsCell as MatchupsCellGeneric,
  StatsCell,
} from '../BenchObjectTableHeader/ui';
import { Breakdown, BreakdownScoreLabel } from '../BreakdownScoreLabel';
import { Projection, ProjectionLabel } from '../ProjectionLabel';
import { SortByPrice } from '../SortByPrice';
import { SortTable } from '../SortTable';
import { StatCellLabel } from './StatCellLabel';

const PlayerName = styled.div`
  width: var(--player-metas-width);
  flex-shrink: 0;
`;

const StatsIcon = styled.div`
  width: var(--player-stats-icon-width);
`;

const LastCellWrapper = styled(Horizontal)`
  width: 100%;
  justify-content: flex-end;
`;

const MatchupsCell = styled(MatchupsCellGeneric)`
  display: none;
  @media ${laptopAndAbove} {
    display: flex;
  }
`;

const getStatsOnboarding = (index: number) => {
  switch (index) {
    case 0:
      return StatsOnboarding.SORT_BY_CLICKING;
    case 1:
      return StatsOnboarding.COMBINE_SORTS;
    default:
      return undefined;
  }
};

type Props = {
  sorts: PrintablePlayerSortName[];
  refine: (_sorts: PrintablePlayerSortName[]) => void;
};

export const SearchBenchObjectTableHeader = ({ sorts, refine }: Props) => {
  const { statsManager, displayableStats, statsSorts } =
    useManagePlayerSearchView();

  return (
    <BenchObjectTableHeader
      firstCell={
        <Horizontal gap={2}>
          <PlayerName>
            <SortTable
              indexes={{
                asc: 'ASC-name_for_sorting',
                desc: 'DESC-name_for_sorting',
              }}
              ascFirst
              sorts={sorts}
              refine={refine}
            >
              <LabelS>
                <FormattedMessage {...cardAttributes.player} />
              </LabelS>
            </SortTable>
          </PlayerName>
          <StatsIcon />
        </Horizontal>
      }
      lastCell={
        <LastCellWrapper>
          <SortByPrice sorts={sorts} refine={refine} />
        </LastCellWrapper>
      }
    >
      <MatchupsCell>
        <LabelS color="var(--c-nd-400)">
          <FormattedMessage {...navLabels.playWithFriends} />
        </LabelS>
      </MatchupsCell>
      {statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE].map(
        (averageScore, i) => (
          <StatsCell key={averageScore}>
            <SortTable
              onboarding={getStatsOnboarding(i)}
              indexes={averageScoreSorts[averageScore]}
              sorts={sorts}
              refine={refine}
            >
              <AveragePlayerScoreLabel mode={averageScore} />
            </SortTable>
          </StatsCell>
        )
      )}
      {statsManager[PlayerSearchViewAttributes.PROJECTION].map(
        (so5FixtureEvent: So5FixtureEvent) =>
          so5FixtureEvent in projectionSorts && (
            <StatsCell key={so5FixtureEvent}>
              <SortTable
                indexes={
                  projectionSorts[
                    so5FixtureEvent as keyof typeof projectionSorts
                  ]
                }
                sorts={sorts}
                refine={refine}
              >
                <ProjectionLabel mode={so5FixtureEvent as Projection} />
              </SortTable>
            </StatsCell>
          )
      )}
      {statsManager[PlayerSearchViewAttributes.BREAKDOWN].map(
        breakdown =>
          breakdown in breakdownScoreSorts && (
            <StatsCell key={breakdown}>
              <SortTable
                indexes={
                  breakdownScoreSorts[
                    breakdown as keyof typeof breakdownScoreSorts
                  ]
                }
                sorts={sorts}
                refine={refine}
              >
                <BreakdownScoreLabel mode={breakdown as Breakdown} />
              </SortTable>
            </StatsCell>
          )
      )}
      {displayableStats.map(stat => {
        return (
          <StatsCell key={stat}>
            <SortTable
              indexes={statsSorts[stat as keyof typeof statsSorts]}
              sorts={sorts}
              refine={refine}
            >
              <StatCellLabel stat={stat} />
            </SortTable>
          </StatsCell>
        );
      })}
    </BenchObjectTableHeader>
  );
};
